"use client";

import React from "react";
import { Separator } from "@/components/ui/separator";
const chapters = [{
  id: "ch1",
  number: "01",
  title: "Introduction",
  content: <>
        <strong>KS Luxury Renovations</strong> ("we", "our", "us"), operating under the brand <strong>KS Luxury Renovations LTD</strong>, is committed to protecting your personal data and respecting your privacy.
        {"\n\n"}This Privacy Policy explains how we collect, use, store, disclose and protect your personal data when you visit our website, contact us, submit an enquiry, request information about our developments, or otherwise interact with us.
        {"\n\n"}We process personal data in accordance with Regulation (EU) 2016/679 (the General Data Protection Regulation or "GDPR"), applicable Cyprus data protection law, and any other mandatory legal requirements that apply to our activities as a data controller.
      </>
}, {
  id: "ch2",
  number: "02",
  title: "Data Controller",
  content: <div className="whitespace-pre-line">
        The data controller responsible for the processing of your personal data is:
        {"\n\n"}<strong>KS Luxury Renovations LTD</strong>
        {"\n"}Registered office: <strong>Aisopou 17, Agios Nikolaos, 3100, Limassol</strong>
        {"\n"}Email: info@ltl.com.cy
        {"\n"}Phone: +357 97 673 308
        {"\n"}Company registration number: 10405651Q
        {"\n\n"}If you contact us through the website, by email or by telephone, KS Luxury Renovations LTD acts as the controller of the personal data you provide in connection with your enquiry or request.
      </div>
}, {
  id: "ch3",
  number: "03",
  title: "What Personal Data We Collect",
  content: <>
        Depending on how you interact with us, we may collect and process the following categories of personal data:
        <div className="mt-4 font-bold">3.1 Information you provide directly</div>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>Full name</li>
          <li>Email address</li>
          <li>Telephone number</li>
          <li>Any message, enquiry, request or other details you submit through our contact forms, email communications or other channels</li>
          <li>Any information you voluntarily provide regarding your interest in a property, development, investment opportunity or viewing request</li>
        </ul>
        <div className="mt-4 font-bold">3.2 Information collected automatically</div>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>IP address</li>
          <li>Browser type and version</li>
          <li>Device type and operating system</li>
          <li>Date and time of visits</li>
          <li>Pages viewed and navigation paths within the website</li>
          <li>Referring website or source</li>
          <li>Basic technical logs used for performance, security and troubleshooting</li>
        </ul>
        <div className="mt-4 font-bold">3.3 Cookies and similar technologies</div>
        {"\n"}We may collect information through cookies and similar technologies as described in our Cookies Policy. Non-essential cookies are only used after you provide valid consent through our cookie banner or cookie preference mechanism.
      </>
}, {
  id: "ch4",
  number: "04",
  title: "How We Use Your Personal Data",
  content: <>
        We use personal data only where we have a valid legal basis and only for legitimate, specified purposes. Depending on the circumstances, we may use your personal data to:
        <ul className="list-disc ml-6 mt-4 space-y-2">
          <li>Respond to your enquiries, requests for information, viewing requests or general communications</li>
          <li>Provide you with information about our developments, properties, services or availability where you have requested it</li>
          <li>Manage and administer our communications with you</li>
          <li>Maintain, secure and improve our website, user experience and website functionality</li>
          <li>Analyze website traffic and performance in aggregate form</li>
          <li>Detect, prevent and investigate technical issues, misuse, fraud or security incidents</li>
          <li>Comply with legal, regulatory and record-keeping obligations</li>
          <li>Establish, exercise or defend legal claims where necessary</li>
        </ul>
      </>
}, {
  id: "ch5",
  number: "05",
  title: "Legal Bases for Processing",
  content: <>
        Under the GDPR, we rely on one or more of the following legal bases depending on the specific processing activity:
        <div className="mt-4 font-bold">5.1 Consent</div>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>When you actively submit your details through a contact form or other enquiry form and ask us to respond</li>
          <li>When you consent to the use of non-essential cookies, such as analytics cookies</li>
          <li>When consent is otherwise specifically requested from you</li>
        </ul>
        <div className="mt-4 font-bold">5.2 Legitimate interests</div>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>Responding to business enquiries and managing customer or prospective customer communications</li>
          <li>Operating, improving and securing our website</li>
          <li>Understanding website performance and user behavior in an aggregated and proportionate way</li>
          <li>Protecting our business from misuse, abuse, fraud and cyber threats</li>
        </ul>
        <div className="mt-4 font-bold">5.3 Legal obligation</div>
        {"\n"}Where processing is necessary to comply with applicable laws, regulations, court orders or lawful requests from public authorities.
      </>
}, {
  id: "ch6",
  number: "06",
  title: "How We Share Personal Data",
  content: <>
        We do not sell or rent your personal data. We may disclose personal data only where necessary and only to the extent required for the purposes described in this policy.
        <ul className="list-disc ml-6 mt-4 space-y-2">
          <li>Website hosting and infrastructure providers</li>
          <li>IT support, website maintenance and cybersecurity providers</li>
          <li>Analytics providers, where you have consented to analytics cookies</li>
          <li>Professional advisers such as lawyers, accountants or auditors where necessary</li>
          <li>Public authorities, regulators, courts or law enforcement bodies where disclosure is required by law or necessary to protect our legal rights</li>
        </ul>
        {"\n"}Where third-party service providers process personal data on our behalf, we require them to do so under appropriate contractual safeguards and to implement suitable security and confidentiality measures.
      </>
}, {
  id: "ch7",
  number: "07",
  title: "International Transfers of Personal Data",
  content: <>
        Some of our service providers may process personal data outside the European Economic Area (EEA). Where personal data is transferred outside the EEA, we will ensure that an appropriate safeguard is in place, such as:
        <ul className="list-disc ml-6 mt-4 space-y-2">
          <li>An adequacy decision adopted by the European Commission</li>
          <li>The European Commission's Standard Contractual Clauses (SCCs)</li>
          <li>Any other lawful transfer mechanism recognised under the GDPR</li>
        </ul>
        {"\n"}The European Commission adopted modernised Standard Contractual Clauses in June 2021 for international data transfers subject to the GDPR. We rely on such safeguards where applicable.
        {"\n\n"}If you would like more information about international data transfer safeguards used by us, you may contact us using the details above.
      </>
}, {
  id: "ch8",
  number: "08",
  title: "Data Retention",
  content: <>
        We retain personal data only for as long as necessary for the purpose for which it was collected, unless a longer retention period is required or permitted by law.
        <ul className="list-disc ml-6 mt-4 space-y-2">
          <li><strong>Contact and enquiry form submissions:</strong> generally up to 12 months after the last meaningful communication, unless an ongoing relationship or legal reason justifies longer retention</li>
          <li><strong>Email correspondence:</strong> for as long as reasonably necessary to manage the enquiry, follow up, or comply with legal or administrative requirements</li>
          <li><strong>Analytics data:</strong> in accordance with the relevant provider's retention settings and only where analytics cookies have been accepted</li>
          <li><strong>Technical and security logs:</strong> for as long as reasonably necessary for security, troubleshooting and system integrity</li>
        </ul>
        {"\n"}When personal data is no longer required, we will delete it, anonymise it, or securely store it only where continued retention is legally required.
      </>
}, {
  id: "ch9",
  number: "09",
  title: "Your Data Protection Rights",
  content: <>
        Subject to the conditions and limitations set by the GDPR and applicable law, you have the right to:
        <ul className="list-disc ml-6 mt-4 space-y-2">
          <li>Request access to your personal data</li>
          <li>Request rectification of inaccurate or incomplete personal data</li>
          <li>Request erasure of your personal data</li>
          <li>Request restriction of processing</li>
          <li>Object to processing carried out on the basis of legitimate interests</li>
          <li>Withdraw consent at any time where processing is based on consent</li>
          <li>Request portability of personal data, where applicable</li>
          <li>Lodge a complaint with a competent supervisory authority</li>
        </ul>
        {"\n"}If you wish to exercise any of these rights, please contact us at info@ltl.com.cy. We may ask you for information necessary to verify your identity before responding to your request.
        {"\n\n"}You also have the right to lodge a complaint with the Office of the Commissioner for Personal Data Protection in Cyprus. Official website: https://www.dataprotection.gov.cy
      </>
}, {
  id: "ch10",
  number: "10",
  title: "Data Security",
  content: <>
        We implement appropriate technical and organisational measures designed to protect personal data against unauthorised or unlawful processing, accidental loss, destruction, damage, disclosure or access.
        <ul className="list-disc ml-6 mt-4 space-y-2">
          <li>Access controls and permissions</li>
          <li>Secure hosting and technical safeguards</li>
          <li>Administrative and operational controls</li>
          <li>Reasonable monitoring and maintenance procedures</li>
        </ul>
        {"\n"}However, no method of transmission over the internet or method of electronic storage is completely secure. While we take reasonable steps to protect personal data, we cannot guarantee absolute security.
      </>
}, {
  id: "ch11",
  number: "11",
  title: "Third-Party Websites",
  content: <>
        Our website may contain links to third-party websites or services. We are not responsible for the privacy practices, policies or content of any third-party website. We encourage you to review the privacy policies of any external sites you visit.
      </>
}, {
  id: "ch12",
  number: "12",
  title: "Children's Data",
  content: <>
        Our website is not directed at children, and we do not knowingly collect personal data from children through the website.
      </>
}, {
  id: "ch13",
  number: "13",
  title: "Changes to this Privacy Policy",
  content: <>
        We may update this Privacy Policy from time to time to reflect legal, technical or business changes. The latest version will always be published on our website with the effective date shown below.
      </>
}, {
  id: "ch14",
  number: "14",
  title: "Contact Us",
  content: <div className="whitespace-pre-line">
        If you have any questions about this Privacy Policy or the way we handle personal data, please contact:
        {"\n\n"}<strong>KS Luxury Renovations LTD</strong>
        {"\n"}Registered office: <strong>Aisopou 17, Agios Nikolaos, 3100, Limassol</strong>
        {"\n"}Email: ks@theksway.com
        {"\n"}Phone: +357 97 673 308
        {"\n"}<strong>Company registration number: 10405651Q</strong>
      </div>
}];
export default function BookContent() {
  return <section data-section-id="185128" id="full-terms" className="bg-background text-foreground py-24">
      <div className="container mx-auto px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          <div className="lg:col-span-8 xl:col-span-9">
            <div className="mt-8">
              <h1 className="font-default font-bold tracking-tight text-secondary !text-5xl">Privacy Policy</h1>
              <span className="font-sans text-lg text-muted-foreground mt-4 block">
                Effective Date: April 2026
              </span>
            </div>

            <Separator className="my-8 bg-border" />

            <div className="space-y-12">
              {chapters.map((chapter, i) => <div key={chapter.id} data-index={i} className="py-4">
                  <span className="font-sans text-sm font-bold tracking-wider text-accent-dark">
                    {chapter.number}
                  </span>
                  <h2 className="font-default font-bold mt-2 text-secondary !text-4xl">
                    {chapter.title}
                  </h2>
                  <div className="font-sans text-base md:text-lg leading-relaxed mt-6 text-muted-foreground whitespace-pre-line">
                    {chapter.content}
                  </div>
                </div>)}
            </div>
          </div>
          
          <div className="hidden lg:block lg:col-span-4 xl:col-span-3">
          </div>
        </div>
      </div>
    </section>;
}